import React from 'react';
import PropTypes from 'prop-types';
import { isEmpty, get } from 'lodash';
import { H2 } from 'components/core/Typography';
import { Breadcrumbs, Crumb } from 'components/core/Breadcrumbs';
import { Card } from 'components/legacy/Card';
import { Accordion } from 'components/search/Accordion';
import MarkdownWrapper from 'components/atoms/MarkdownWrapper/MarkdownWrapper';
import TrustPilotReviewsSearchContent from 'components/search/TrustPilotReviewsSearchContent/TrustPilotReviewsSearchContent';
import BestPricedUnits from './BestPricedUnits';
import CityLinks from './CityLinks';
import NearbyCities from './NearbyCities';
import { AveragePricesContent } from './AveragePricesContent';
import nmpReviews from './nmpReviews';
import { amenityObject, faqTitleSuffixMap } from 'config/content';
import { INTERNAL_PAGE_TYPES } from '../../../config/constants';

import './searchContent.scss';

export default function SearchContent({
	content,
	location,
	pageType,
	faqContent,
	faqShowing,
	averageMonthlyUnitPrice,
	amenityType,
	cityUrl,
	stateUrl,
	baseUrl,
	headline,
	searchType,
	subPageType,
}) {
	const seoBodyJson = (content.seoBodyJson || []).filter(
		(item) => item.type !== 'faq_section',
	);

	const hasTpReviews = !!content?.tpReviews?.length;
	const { city, state } = location;
	const isNMP = pageType === INTERNAL_PAGE_TYPES.NEAR_ME_SEARCH_PAGE;

	const faqTitleSuffix = faqTitleSuffixMap[amenityType] || 'Self Storage';
	const faqAccordionTitle = isNMP
		? `FAQs About ${faqTitleSuffix}`
		: `Frequently Asked Questions About ${city}, ${state} ${faqTitleSuffix}`;

	return (
		<div className="search-content">
			{!isNMP && (
				<>
					<H2
						className="seo-content-header"
						size="mediumLarge"
						weight="extraBold"
						color="darkGray"
					>
						{`${city}, ${state} ${amenityObject[amenityType]} Self Storage Information`}
					</H2>
					{seoBodyJson.length > 0 && (
						<Card>
							<MarkdownWrapper content={seoBodyJson} />
						</Card>
					)}
					{faqShowing && (
						<Card>
							<Accordion
								accordionTitle={faqAccordionTitle}
								content={faqContent}
								isFAQ
								className="faq-content"
								preserve
							/>
						</Card>
					)}
					{(!isEmpty(averageMonthlyUnitPrice) ||
						(pageType === INTERNAL_PAGE_TYPES.CITY_LANDING_PAGE &&
							!isEmpty(location))) && (
						<H2
							className="seo-content-heading"
							size="mediumLarge"
							weight="extraBold"
							color="darkGray"
						>
							{`Self Storage Prices in ${city}, ${state}`}
						</H2>
					)}
					{!isEmpty(averageMonthlyUnitPrice) && (
						<AveragePricesContent
							data={averageMonthlyUnitPrice}
							city={city}
						/>
					)}
					{pageType === INTERNAL_PAGE_TYPES.CITY_LANDING_PAGE &&
						!isEmpty(location) && (
							<>
								<Card className="best-priced-units">
									<div className="card-content">
										<BestPricedUnits
											location={location}
											pricingStats={content.pricingStats}
										/>
									</div>
								</Card>
							</>
						)}
					{!isEmpty(get(content, 'nearbyCities')) && (
						<NearbyCities
							title={`Browse Self Storage in Nearby ${location.fullState} Cities`}
							links={content.nearbyCities}
						/>
					)}
					{!isEmpty(get(content, 'cityLinks.amenities')) && (
						<CityLinks
							title={`Browse ${location.city} Self Storage by Amenity`}
							links={content.cityLinks.amenities}
							city={location.city}
						/>
					)}
					{!isEmpty(get(content, 'cityLinks.vehicleStorage')) && (
						<CityLinks
							title={`Browse ${location.city} Self Storage by Vehicle`}
							links={content.cityLinks.vehicleStorage}
							city={location.city}
						/>
					)}

					{hasTpReviews && (
						<TrustPilotReviewsSearchContent
							reviews={content.tpReviews}
						/>
					)}
				</>
			)}

			{isNMP && (
				<>
					{faqShowing && (
						<Card>
							<Accordion
								accordionTitle={faqAccordionTitle}
								content={faqContent}
								isFAQ
								className="faq-content"
								preserve
							/>
						</Card>
					)}
					{/* TODO: NMP content sections (Why Choose, Helpful Tips, Helpful Resources)
					    blocked on content source — PITA integration or static mapping tables.
					    See SEO Spec section 12 + .planning/phases/01-near-me-frontend-ssdr-1-4-pw/01-04-PLAN.md */}
					{nmpReviews[subPageType] && (
						<TrustPilotReviewsSearchContent
							reviews={nmpReviews[subPageType]}
							title="Real Reviews of SelfStorage.com"
						/>
					)}
				</>
			)}

			{isNMP ? (
				// The enhanced Breadcrumbs HOC (default export) always prepends a Home
				// crumb from baseUrl before merging the crumbs prop, so the final trail
				// rendered is: Home → NMP crumb.
				<Breadcrumbs
					baseUrl={baseUrl}
					location={{}}
					suppressSchema
					className="nmp-breadcrumbs"
					crumbs={[
						<Crumb
							key="crumb-nmp"
							href={`${(baseUrl || '').replace(/\/$/, '')}${subPageType ? `/self-storage-near-me/${subPageType}/` : '/self-storage-near-me/'}`}
							id={`${(baseUrl || '').replace(/\/$/, '')}${subPageType ? `/self-storage-near-me/${subPageType}/` : '/self-storage-near-me/'}`}
							label={headline || 'Self Storage Near Me'}
							name={headline || 'Self Storage Near Me'}
							isLast
						/>,
					]}
				/>
			) : (
				<Breadcrumbs
					baseUrl={baseUrl}
					cityUrl={cityUrl}
					stateUrl={stateUrl}
					searchType={searchType}
					location={location}
					isCLPPage={!isNMP}
				/>
			)}
		</div>
	);
}

SearchContent.propTypes = {
	content: PropTypes.shape({
		seoBodyJson: PropTypes.arrayOf(PropTypes.object),
		tpReviews: PropTypes.array,
		pricingStats: PropTypes.object,
		nearbyCities: PropTypes.arrayOf(PropTypes.object),
		cityLinks: PropTypes.object,
		bestPricedUnits: PropTypes.arrayOf(PropTypes.object),
	}),
	location: PropTypes.shape({
		city: PropTypes.string,
		state: PropTypes.string,
		fullState: PropTypes.string,
	}).isRequired,
	pageType: PropTypes.string,
	subPageType: PropTypes.string,
	drillDownLinks: PropTypes.object,
	searchParams: PropTypes.shape({
		type: PropTypes.string,
		vehicle: PropTypes.string,
	}).isRequired,
	faqContent: PropTypes.arrayOf(PropTypes.object),
	faqShowing: PropTypes.bool,
	averageMonthlyUnitPrice: PropTypes.object,
	amenityType: PropTypes.string,
	cityUrl: PropTypes.string,
	stateUrl: PropTypes.string,
	baseUrl: PropTypes.string,
	headline: PropTypes.string,
	searchType: PropTypes.string,
};
